import { useState, useCallback, useMemo } from 'react';

const MAX_IMAGES = 5;

let nextId = 0;
const makeId = () => {
  nextId += 1;
  return `img-${Date.now()}-${nextId}`;
};

export const useImageHistory = () => {
  const [ images, setImages ] = useState([]);
  const [ activeId, setActiveId ] = useState(null);
  const [ past, setPast ] = useState([]);
  const [ future, setFuture ] = useState([]);

  const active = useMemo(
    () => images.find(img => img.id === activeId) || null,
    [ images, activeId ],
  );

  const addImages = useCallback(srcs => {
    const list = Array.isArray(srcs) ? srcs : [ srcs ];
    if (list.length === 0) return { added: [], droppedCount: 0 };

    let added = [];
    let droppedCount = 0;

    setImages(prev => {
      const room = MAX_IMAGES - prev.length;
      const accepted = list.slice(0, Math.max(0, room));
      droppedCount = list.length - accepted.length;
      added = accepted.map(({ src, name }) => ({
        id: makeId(),
        original: src,
        current: src,
        name: name || 'Untitled Image',
      }));
      const next = [ ...prev, ...added ];
      if (next.length > 0 && !prev.some(p => p.id === activeId)) {
        setActiveId(added[0]?.id ?? next[0].id);
      }
      return next;
    });

    return { added, droppedCount };
  }, [ activeId ]);

  const load = useCallback(srcs => {
    setPast([]);
    setFuture([]);
    const list = Array.isArray(srcs) ? srcs : [ { src: srcs, name: undefined } ];
    const capped = list.slice(0, MAX_IMAGES);
    const fresh = capped.map(({ src, name }) => ({
      id: makeId(),
      original: src,
      current: src,
      name: name || 'Untitled Image',
    }));
    setImages(fresh);
    setActiveId(fresh[0]?.id ?? null);
    return {
      added: fresh,
      droppedCount: Math.max(0, list.length - capped.length),
    };
  }, []);

  const removeImage = useCallback(id => {
    setImages(prev => {
      const next = prev.filter(img => img.id !== id);
      if (id === activeId) {
        setActiveId(next[0]?.id ?? null);
      }
      return next;
    });
    setPast(prev => prev.filter(entry => entry.imageId !== id));
    setFuture(prev => prev.filter(entry => entry.imageId !== id));
  }, [ activeId ]);

  const applyEdit = useCallback((imageId, src) => {
    if (!src) return;
    const targetId = imageId || activeId;
    if (!targetId) return;

    setImages(prev => {
      const target = prev.find(img => img.id === targetId);
      if (!target || target.current === src) return prev;

      setPast(p => [ ...p, { imageId: targetId, prevUrl: target.current, nextUrl: src } ]);
      setFuture([]);

      return prev.map(img => (img.id === targetId ? { ...img, current: src } : img));
    });
  }, [ activeId ]);

  const undo = useCallback(() => {
    setPast(prev => {
      if (prev.length === 0) return prev;
      const entry = prev[prev.length - 1];
      setImages(imgs => imgs.map(img => (
        img.id === entry.imageId ? { ...img, current: entry.prevUrl } : img
      )));
      setActiveId(entry.imageId);
      setFuture(f => [ entry, ...f ]);
      return prev.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuture(prev => {
      if (prev.length === 0) return prev;
      const [ entry, ...rest ] = prev;
      setImages(imgs => imgs.map(img => (
        img.id === entry.imageId ? { ...img, current: entry.nextUrl } : img
      )));
      setActiveId(entry.imageId);
      setPast(p => [ ...p, entry ]);
      return rest;
    });
  }, []);

  const reset = useCallback(() => {
    setImages(prev => prev.map(img => ({ ...img, current: img.original })));
    setPast([]);
    setFuture([]);
  }, []);

  const canReset = useMemo(
    () => images.some(img => img.current !== img.original),
    [ images ],
  );

  return {
    images,
    activeId,
    setActiveId,
    active,
    original: active?.original ?? null,
    current: active?.current ?? null,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    canReset,
    load,
    addImages,
    removeImage,
    applyEdit,
    undo,
    redo,
    reset,
    maxImages: MAX_IMAGES,
  };
};
